import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  IdentificationCard,
  CheckCircle,
  Clock,
  Warning,
  X,
  Eye,
  Download,
  File,
  User,
  Buildings,
  Bank,
  FileText,
  MagnifyingGlass,
  ArrowLeft
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  submitted: { label: 'Submitted', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  under_review: { label: 'Under Review', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700 border-green-200' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700 border-red-200' },
  resubmission_required: { label: 'Resubmission', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  pending: { label: 'Not Started', color: 'bg-gray-100 text-gray-500 border-gray-200' },
};

const DOCUMENT_TYPE_LABELS = {
  gst_certificate: 'GST Certificate',
  pan_card: 'PAN Card',
  cancelled_cheque: 'Cancelled Cheque',
  address_proof: 'Address Proof',
  business_registration: 'Business Registration',
  bank_statement: 'Bank Statement',
  other: 'Other Document',
};

export default function AdminKycPage() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedKyc, setSelectedKyc] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionNote, setActionNote] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [resubReason, setResubReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionDialog, setActionDialog] = useState(null);

  const fetchApplications = useCallback(async () => {
    try {
      const url = statusFilter === 'all'
        ? `${API_URL}/api/admin/kyc/all`
        : `${API_URL}/api/admin/kyc/all?status=${statusFilter}`;
      const res = await axios.get(url, { withCredentials: true });
      setApplications(res.data.applications || []);
    } catch (error) {
      toast.error('Failed to load KYC applications');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const fetchDetail = async (kycId) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/kyc/${kycId}`, { withCredentials: true });
      setSelectedKyc(res.data);
    } catch (error) {
      toast.error('Failed to load KYC detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleStartReview = async (kycId) => {
    try {
      await axios.post(`${API_URL}/api/admin/kyc/${kycId}/review`, {}, { withCredentials: true });
      toast.success('Review started');
      fetchApplications();
      if (selectedKyc) fetchDetail(kycId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start review');
    }
  };

  const handleApprove = async () => {
    if (!selectedKyc) return;
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/kyc/${selectedKyc.kyc_id}/approve`, {
        note: actionNote || 'KYC approved'
      }, { withCredentials: true });
      toast.success('KYC approved successfully');
      setActionDialog(null);
      setActionNote('');
      fetchApplications();
      fetchDetail(selectedKyc.kyc_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedKyc || !rejectReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/kyc/${selectedKyc.kyc_id}/reject`, {
        reason: rejectReason
      }, { withCredentials: true });
      toast.success('KYC rejected');
      setActionDialog(null);
      setRejectReason('');
      fetchApplications();
      fetchDetail(selectedKyc.kyc_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestResubmission = async () => {
    if (!selectedKyc || !resubReason.trim()) {
      toast.error('Reason is required');
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/kyc/${selectedKyc.kyc_id}/request-resubmission`, {
        reason: resubReason
      }, { withCredentials: true });
      toast.success('Resubmission requested');
      setActionDialog(null);
      setResubReason('');
      fetchApplications();
      fetchDetail(selectedKyc.kyc_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request resubmission');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredApplications = applications.filter(app => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      app.business_name?.toLowerCase().includes(q) ||
      app.seller?.name?.toLowerCase().includes(q) ||
      app.seller?.email?.toLowerCase().includes(q) ||
      app.gst_number?.toLowerCase().includes(q)
    );
  });

  const statusCounts = applications.reduce((acc, app) => {
    acc[app.status] = (acc[app.status] || 0) + 1;
    return acc;
  }, {});

  if (selectedKyc && !detailLoading) {
    return (
      <DashboardLayout title="KYC Review" subtitle="Application details">
        <button
          onClick={() => setSelectedKyc(null)}
          className="flex items-center gap-2 text-sm text-[#5a5c87] hover:text-[#0A0A0A] mb-6 transition-colors"
          data-testid="back-to-list"
        >
          <ArrowLeft size={16} /> Back to applications
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Status Bar */}
            <div className="bg-white border border-[#e5e5ea] p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className={`px-3 py-1 text-xs font-bold rounded border ${STATUS_CONFIG[selectedKyc.status]?.color || STATUS_CONFIG.pending.color}`}>
                  {STATUS_CONFIG[selectedKyc.status]?.label || selectedKyc.status}
                </span>
                <span className="text-sm text-[#5a5c87]">ID: {selectedKyc.kyc_id}</span>
              </div>
              <div className="flex gap-2">
                {selectedKyc.status === 'submitted' && (
                  <Button
                    size="sm"
                    onClick={() => handleStartReview(selectedKyc.kyc_id)}
                    className="bg-[#7d64ff] hover:bg-[#6153a7] text-white"
                    data-testid="start-review-btn"
                  >
                    Start Review
                  </Button>
                )}
                {['submitted', 'under_review'].includes(selectedKyc.status) && (
                  <>
                    <Button size="sm" onClick={() => setActionDialog('approve')} className="bg-[#28cd8c] hover:bg-[#1fb076] text-white" data-testid="approve-btn">
                      Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setActionDialog('reject')} className="border-[#ea4b5a] text-[#ea4b5a] hover:bg-[#ea4b5a]/5" data-testid="reject-btn">
                      Reject
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setActionDialog('resubmit')} className="border-[#FF9500] text-[#FF9500] hover:bg-[#FF9500]/5" data-testid="resubmit-btn">
                      Request Changes
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Business Info */}
            <div className="bg-white border border-[#e5e5ea] p-6">
              <h3 className="font-bold text-base mb-4 flex items-center gap-2">
                <Buildings size={20} className="text-[#7d64ff]" /> Business Information
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-[#5a5c87] block text-xs uppercase tracking-wider mb-1">Business Name</span>
                  <span className="font-semibold">{selectedKyc.business_name}</span>
                </div>
                <div>
                  <span className="text-[#5a5c87] block text-xs uppercase tracking-wider mb-1">Business Type</span>
                  <span className="font-semibold">{selectedKyc.business_type}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-[#5a5c87] block text-xs uppercase tracking-wider mb-1">Address</span>
                  <span className="font-semibold">
                    {[selectedKyc.business_address, selectedKyc.business_city, selectedKyc.business_state, selectedKyc.business_pincode].filter(Boolean).join(', ')}
                  </span>
                </div>
              </div>
            </div>

            {/* Tax Info */}
            <div className="bg-white border border-[#e5e5ea] p-6">
              <h3 className="font-bold text-base mb-4 flex items-center gap-2">
                <FileText size={20} className="text-[#7d64ff]" /> Tax Information
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-[#5a5c87] block text-xs uppercase tracking-wider mb-1">GST Number</span>
                  <span className="font-mono font-semibold">{selectedKyc.gst_number}</span>
                </div>
                <div>
                  <span className="text-[#5a5c87] block text-xs uppercase tracking-wider mb-1">PAN Number</span>
                  <span className="font-mono font-semibold">{selectedKyc.pan_number}</span>
                </div>
              </div>
            </div>

            {/* Bank Info */}
            <div className="bg-white border border-[#e5e5ea] p-6">
              <h3 className="font-bold text-base mb-4 flex items-center gap-2">
                <Bank size={20} className="text-[#7d64ff]" /> Bank Details
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-[#5a5c87] block text-xs uppercase tracking-wider mb-1">Bank Name</span>
                  <span className="font-semibold">{selectedKyc.bank_name}</span>
                </div>
                <div>
                  <span className="text-[#5a5c87] block text-xs uppercase tracking-wider mb-1">IFSC Code</span>
                  <span className="font-mono font-semibold">{selectedKyc.bank_ifsc}</span>
                </div>
                <div>
                  <span className="text-[#5a5c87] block text-xs uppercase tracking-wider mb-1">Account Number</span>
                  <span className="font-mono font-semibold">{selectedKyc.bank_account_number}</span>
                </div>
                <div>
                  <span className="text-[#5a5c87] block text-xs uppercase tracking-wider mb-1">Account Holder</span>
                  <span className="font-semibold">{selectedKyc.bank_account_holder}</span>
                </div>
              </div>
            </div>

            {/* Documents */}
            <div className="bg-white border border-[#e5e5ea] p-6">
              <h3 className="font-bold text-base mb-4 flex items-center gap-2">
                <File size={20} className="text-[#7d64ff]" /> Uploaded Documents
              </h3>
              {selectedKyc.document_files?.length > 0 ? (
                <div className="space-y-2">
                  {selectedKyc.document_files.map(doc => (
                    <div key={doc.document_id} className="flex items-center justify-between p-3 bg-[#fcfcfc] border border-[#e5e5ea] rounded">
                      <div className="flex items-center gap-3">
                        <File size={20} className="text-[#7d64ff]" />
                        <div>
                          <p className="font-medium text-sm">{doc.original_filename}</p>
                          <p className="text-xs text-[#5a5c87]">{DOCUMENT_TYPE_LABELS[doc.document_type] || doc.document_type}</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(`${API_URL}/api/kyc/document/${doc.document_id}`, '_blank')}
                        data-testid={`view-doc-${doc.document_id}`}
                      >
                        <Eye size={16} className="mr-1" /> View
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[#5a5c87]">No documents uploaded</p>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Seller Info */}
            <div className="bg-white border border-[#e5e5ea] p-6">
              <h3 className="font-bold text-base mb-4 flex items-center gap-2">
                <User size={20} className="text-[#7d64ff]" /> Seller
              </h3>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="text-[#5a5c87] text-xs uppercase tracking-wider">Name</span>
                  <p className="font-semibold">{selectedKyc.seller?.name}</p>
                </div>
                <div>
                  <span className="text-[#5a5c87] text-xs uppercase tracking-wider">Email</span>
                  <p className="font-semibold">{selectedKyc.seller?.email}</p>
                </div>
                <div>
                  <span className="text-[#5a5c87] text-xs uppercase tracking-wider">Phone</span>
                  <p className="font-semibold">{selectedKyc.seller?.phone || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-[#5a5c87] text-xs uppercase tracking-wider">Joined</span>
                  <p className="font-semibold">
                    {selectedKyc.seller?.created_at ? new Date(selectedKyc.seller.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="bg-white border border-[#e5e5ea] p-6">
              <h3 className="font-bold text-base mb-4">Timeline</h3>
              <div className="space-y-4">
                {selectedKyc.submitted_at && (
                  <div className="flex gap-3">
                    <div className="w-2 h-2 mt-2 rounded-full bg-blue-500 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium">Submitted</p>
                      <p className="text-xs text-[#5a5c87]">{new Date(selectedKyc.submitted_at).toLocaleString()}</p>
                    </div>
                  </div>
                )}
                {selectedKyc.review_started_at && (
                  <div className="flex gap-3">
                    <div className="w-2 h-2 mt-2 rounded-full bg-yellow-500 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium">Review Started</p>
                      <p className="text-xs text-[#5a5c87]">{new Date(selectedKyc.review_started_at).toLocaleString()}</p>
                    </div>
                  </div>
                )}
                {selectedKyc.review_notes?.map((note, idx) => (
                  <div key={idx} className="flex gap-3">
                    <div className={`w-2 h-2 mt-2 rounded-full flex-shrink-0 ${
                      note.action === 'approved' ? 'bg-green-500' :
                      note.action === 'rejected' ? 'bg-red-500' : 'bg-orange-500'
                    }`} />
                    <div>
                      <p className="text-sm font-medium capitalize">{note.action?.replace('_', ' ')}</p>
                      <p className="text-xs text-[#5a5c87]">{note.note}</p>
                      <p className="text-xs text-[#5a5c87]">
                        {note.reviewer_name} - {new Date(note.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Action Dialogs */}
        <Dialog open={actionDialog === 'approve'} onOpenChange={() => setActionDialog(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-bold">Approve KYC</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-[#5a5c87]">Approve KYC for <strong>{selectedKyc.business_name}</strong>?</p>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Note (Optional)</Label>
                <Textarea value={actionNote} onChange={e => setActionNote(e.target.value)} placeholder="Add a note..." rows={3} className="mt-1" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setActionDialog(null)}>Cancel</Button>
                <Button onClick={handleApprove} disabled={actionLoading} className="bg-[#28cd8c] hover:bg-[#1fb076] text-white" data-testid="confirm-approve-btn">
                  {actionLoading ? 'Approving...' : 'Approve'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={actionDialog === 'reject'} onOpenChange={() => setActionDialog(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-bold">Reject KYC</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-[#5a5c87]">Reject KYC for <strong>{selectedKyc.business_name}</strong>?</p>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Reason *</Label>
                <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Why is this being rejected?" rows={3} className="mt-1" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setActionDialog(null)}>Cancel</Button>
                <Button onClick={handleReject} disabled={actionLoading || !rejectReason.trim()} className="bg-[#ea4b5a] hover:bg-[#c43447] text-white" data-testid="confirm-reject-btn">
                  {actionLoading ? 'Rejecting...' : 'Reject'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={actionDialog === 'resubmit'} onOpenChange={() => setActionDialog(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-bold">Request Resubmission</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-[#5a5c87]">Request changes from <strong>{selectedKyc.business_name}</strong>?</p>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">What needs to be changed? *</Label>
                <Textarea value={resubReason} onChange={e => setResubReason(e.target.value)} placeholder="Specify what changes are needed..." rows={3} className="mt-1" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setActionDialog(null)}>Cancel</Button>
                <Button onClick={handleRequestResubmission} disabled={actionLoading || !resubReason.trim()} className="bg-[#FF9500] hover:bg-[#CC7A00] text-white" data-testid="confirm-resubmit-btn">
                  {actionLoading ? 'Sending...' : 'Request Changes'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="KYC Management" subtitle="Review and manage seller KYC applications">
      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {[
          { key: 'all', label: 'Total', count: applications.length, color: 'text-[#0A0A0A]' },
          { key: 'submitted', label: 'Pending', count: statusCounts.submitted || 0, color: 'text-blue-600' },
          { key: 'under_review', label: 'In Review', count: statusCounts.under_review || 0, color: 'text-yellow-600' },
          { key: 'approved', label: 'Approved', count: statusCounts.approved || 0, color: 'text-green-600' },
          { key: 'rejected', label: 'Rejected', count: (statusCounts.rejected || 0) + (statusCounts.resubmission_required || 0), color: 'text-red-600' },
        ].map(stat => (
          <button
            key={stat.key}
            onClick={() => setStatusFilter(stat.key)}
            className={`bg-white border p-4 text-left transition-all ${
              statusFilter === stat.key ? 'border-[#7d64ff] ring-1 ring-[#7d64ff]' : 'border-[#e5e5ea] hover:border-[#7d64ff]/40'
            }`}
            data-testid={`filter-${stat.key}`}
          >
            <p className={`text-2xl font-bold ${stat.color}`}>{stat.count}</p>
            <p className="text-xs text-[#5a5c87] uppercase tracking-wider mt-1">{stat.label}</p>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="bg-white border border-[#e5e5ea] p-4 mb-4">
        <div className="relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5a5c87]" />
          <Input
            placeholder="Search by business name, seller name, email, or GST..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-10 rounded-sm"
            data-testid="kyc-search"
          />
        </div>
      </div>

      {/* Applications Table */}
      <div className="bg-white border border-[#e5e5ea]">
        {loading ? (
          <div className="p-12 text-center">
            <div className="spinner mx-auto"></div>
          </div>
        ) : filteredApplications.length === 0 ? (
          <div className="p-12 text-center">
            <IdentificationCard size={48} className="text-[#e5e5ea] mx-auto mb-4" />
            <p className="text-[#5a5c87]">No KYC applications found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#e5e5ea]">
                  <th className="text-left text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87] p-4">Seller</th>
                  <th className="text-left text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87] p-4">Business</th>
                  <th className="text-left text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87] p-4">GST</th>
                  <th className="text-left text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87] p-4">Docs</th>
                  <th className="text-left text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87] p-4">Status</th>
                  <th className="text-left text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87] p-4">Submitted</th>
                  <th className="text-right text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87] p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredApplications.map(app => (
                  <tr key={app.kyc_id} className="border-b border-[#e5e5ea] hover:bg-[#fcfcfc] transition-colors">
                    <td className="p-4">
                      <p className="font-semibold text-sm">{app.seller?.name}</p>
                      <p className="text-xs text-[#5a5c87]">{app.seller?.email}</p>
                    </td>
                    <td className="p-4">
                      <p className="font-medium text-sm">{app.business_name}</p>
                      <p className="text-xs text-[#5a5c87]">{app.business_type}</p>
                    </td>
                    <td className="p-4 font-mono text-sm">{app.gst_number}</td>
                    <td className="p-4 text-sm">{app.documents?.length || 0}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 text-xs font-bold rounded border ${STATUS_CONFIG[app.status]?.color || STATUS_CONFIG.pending.color}`}>
                        {STATUS_CONFIG[app.status]?.label || app.status}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-[#5a5c87]">
                      {app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="p-4 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => fetchDetail(app.kyc_id)}
                        data-testid={`review-${app.kyc_id}`}
                      >
                        <Eye size={16} className="mr-1" /> Review
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
